import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGoldPrice } from "@/hooks/useGoldPrice";
import { ArrowRight } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { FAQ } from "@/components/site/article/FAQ";
import { DeskCallout } from "@/components/site/DeskCallout";

const SITE = "https://xauproacademy.com";
const PATH = "/gold-pip-calculator";
const PAGE_URL = `${SITE}${PATH}`;
const TITLE = "Gold Pip Calculator: XAUUSD Pip Value and Profit | XAU Pro Academy";
const DESC =
  "Free XAUUSD pip value calculator. Choose your broker's pip definition, enter lots, entry and exit to get pip value, profit or loss, spread cost and R multiple. Includes a gold pip value table.";

type PipDef = { id: string; size: number; label: string; note: string };
const PIP_DEFS: PipDef[] = [
  { id: "0.01", size: 0.01, label: "0.01 (point)", note: "Common on MT4/MT5 brokers that quote two decimals and count every cent as a pip." },
  { id: "0.10", size: 0.1, label: "0.10 (standard)", note: "The most widely used retail convention: a move from 2,400.00 to 2,400.10 is one pip." },
  { id: "1.00", size: 1, label: "1.00 (dollar)", note: "Used by some desks and educators who count one full dollar of price as a pip." },
];

const FAQS = [
  { q: "How much is 1 pip in gold?", a: "With the common 0.10 pip definition, one pip on 1 standard lot (100 oz) is worth $10, on 0.10 lots $1 and on 0.01 lots $0.10. If your broker defines a pip as 0.01, divide those values by ten." },
  { q: "How do you calculate pip value for XAUUSD?", a: "Pip value = pip size x contract size x lots. With a 0.10 pip, 100 oz per lot and 0.50 lots: 0.10 x 100 x 0.50 = $5 per pip. Because gold is quoted in US dollars, no currency conversion is needed for a USD account." },
  { q: "How many pips is a $10 move in gold?", a: "100 pips with the 0.10 convention, 1,000 pips with the 0.01 convention and 10 pips with the 1.00 convention. The dollar profit is the same in every case: $1,000 per standard lot." },
  { q: "Why do gold pip calculators give different answers?", a: "Brokers do not agree on what a gold pip is. The dollar outcome of a trade never changes, only the pip count. This calculator lets you pick your broker's definition and always shows the dollar move per ounce as the neutral reference." },
  { q: "How much profit is 100 pips on gold?", a: "Using the 0.10 pip, 100 pips is a $10 move per ounce: $1,000 on 1 lot, $100 on 0.10 lots and $10 on 0.01 lots, before spread and commission." },
  { q: "What is the difference between this and a lot size calculator?", a: "A pip calculator answers what a given position is worth per pip and what it makes or loses between two prices. A lot size calculator works backwards from a risk budget and stop distance to the correct position size. Use the lot size calculator before entry and this tool to read the trade." },
];

const SECTIONS = [
  ["calculator", "Calculator"],
  ["definitions", "Pip definitions"],
  ["table", "Pip value table"],
  ["formula", "Formula"],
  ["costs", "Spread cost"],
  ["faq", "FAQ"],
] as const;

export const Route = createFileRoute("/gold-pip-calculator")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "gold pip calculator, xauusd pip calculator, gold pip value, xauusd pip value, how much is 1 pip in gold, gold profit calculator, xauusd profit calculator" },
      { property: "og:title", content: "Gold Pip Calculator: XAUUSD Pip Value and Profit" },
      { property: "og:description", content: "Pick your broker's gold pip definition and get exact pip value, profit, spread cost and R multiple for any XAUUSD position." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Gold Pip Calculator (XAUUSD)",
          url: PAGE_URL,
          applicationCategory: "FinanceApplication",
          operatingSystem: "Any",
          description: DESC, featureList: ["XAUUSD pip value per lot", "Profit and loss in USD", "Account currency conversion"], browserRequirements: "Requires JavaScript. Modern HTML5 browser", softwareVersion: "2026.1", isAccessibleForFree: true, inLanguage: "en",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/OnlineOnly" },
          publisher: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Gold Pip Calculator", item: PAGE_URL },
          ],
        }),
      },
    ],
  }),
  component: GoldPipCalculatorPage,
});

const usd = (x: number) =>
  (x < 0 ? "-$" : "$") + Math.abs(x).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (x: number, d = 1) => x.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

function Field({ label, value, onChange, step, min }: { label: string; value: number; onChange: (v: number) => void; step: number; min?: number }) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        value={Number.isFinite(value) ? value : ""}
        step={step}
        min={min}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm text-foreground focus:border-gold focus:outline-none"
      />
    </label>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "pos" | "neg" }) {
  const color = tone === "pos" ? "var(--success, hsl(142 60% 45%))" : tone === "neg" ? "var(--destructive)" : undefined;
  return (
    <div className="rounded-md border border-border bg-background p-3">
      <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-lg font-semibold text-foreground" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}

function PipCalculator() {
  const [pipId, setPipId] = useState("0.10");
  const [lots, setLots] = useState(0.5);
  const [contract, setContract] = useState(100);
  const [dir, setDir] = useState<"long" | "short">("long");
  const [entry, setEntry] = useState(2400);
  const [exit, setExit] = useState(2440);
  const [stop, setStop] = useState(2380);
  const [spread, setSpread] = useState(0.3);
  const pip = PIP_DEFS.find((p) => p.id === pipId)!;
  const live = useGoldPrice(60_000);
  const seeded = useRef(false);

  // Seed entry, target (+$40) and stop (-$20) from the live spot price once.
  useEffect(() => {
    if (seeded.current || !live.price) return;
    seeded.current = true;
    const p = Math.round(live.price * 100) / 100;
    setEntry(p);
    setExit(Math.round((p + 40) * 100) / 100);
    setStop(Math.round((p - 20) * 100) / 100);
  }, [live.price]);

  const switchDir = (d: "long" | "short") => {
    if (d === dir) return;
    setDir(d);
    // Mirror target and stop around entry so the example stays valid.
    setExit(Math.round((2 * entry - exit) * 100) / 100);
    setStop(Math.round((2 * entry - stop) * 100) / 100);
  };

  const r = useMemo(() => {
    const oz = (lots || 0) * (contract || 0);
    const sign = dir === "long" ? 1 : -1;
    const move = ((exit || 0) - (entry || 0)) * sign;
    const risk = ((entry || 0) - (stop || 0)) * sign;
    const gross = move * oz;
    const spreadCost = (spread || 0) * oz;
    return {
      oz,
      pipValue: pip.size * oz,
      dollarValue: oz,
      move,
      pips: move / pip.size,
      gross,
      spreadCost,
      net: gross - spreadCost,
      riskUsd: risk > 0 ? risk * oz : null,
      rMultiple: risk > 0 ? move / risk : null,
      validStop: risk > 0,
    };
  }, [lots, contract, dir, entry, exit, stop, spread, pip.size]);

  return (
    <div className="grid gap-6 rounded-lg border border-border bg-card p-5 lg:grid-cols-2">
      <div className="space-y-4">
        <div>
          <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Broker pip definition</span>
          <div className="mt-1 flex flex-wrap gap-2" role="group" aria-label="Pip definition">
            {PIP_DEFS.map((p) => (
              <Button key={p.id} size="sm" variant={pipId === p.id ? "gold" : "outline"} aria-pressed={pipId === p.id} onClick={() => setPipId(p.id)}>{p.label}</Button>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{pip.note}</p>
        </div>
        <div className="flex gap-2" role="group" aria-label="Direction">
          <Button size="sm" variant={dir === "long" ? "gold" : "outline"} aria-pressed={dir === "long"} onClick={() => switchDir("long")}>Buy (long)</Button>
          <Button size="sm" variant={dir === "short" ? "gold" : "outline"} aria-pressed={dir === "short"} onClick={() => switchDir("short")}>Sell (short)</Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Lots" value={lots} onChange={setLots} step={0.01} min={0} />
          <Field label="Oz per lot" value={contract} onChange={setContract} step={1} min={0} />
          <Field label="Entry price" value={entry} onChange={setEntry} step={0.1} />
          <Field label="Exit / target price" value={exit} onChange={setExit} step={0.1} />
          <Field label="Stop loss price" value={stop} onChange={setStop} step={0.1} />
          <Field label="Spread ($ per oz)" value={spread} onChange={setSpread} step={0.01} min={0} />
        </div>
        <p className="text-xs text-muted-foreground">{live.price ? `Prefilled from live spot ${live.price.toFixed(2)}. ` : ""}Most brokers use 100 oz per standard lot. Confirm your contract specification before trading.</p>
      </div>
      <div className="space-y-3" aria-live="polite">
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Pip value" value={usd(r.pipValue)} />
          <Stat label="Value of $1 move" value={usd(r.dollarValue)} />
          <Stat label="Position size" value={`${num(r.oz, 2)} oz`} />
          <Stat label="Pips to exit" value={num(r.pips, 1)} tone={r.pips >= 0 ? "pos" : "neg"} />
          <Stat label="Gross P/L" value={usd(r.gross)} tone={r.gross >= 0 ? "pos" : "neg"} />
          <Stat label="Net P/L after spread" value={usd(r.net)} tone={r.net >= 0 ? "pos" : "neg"} />
          <Stat label="Risk to stop" value={r.riskUsd !== null ? usd(r.riskUsd) : "Invalid stop"} tone={r.riskUsd !== null ? "neg" : undefined} />
          <Stat label="R multiple" value={r.rMultiple !== null ? `${num(r.rMultiple, 2)}R` : "n/a"} />
        </div>
        <p className="text-xs text-muted-foreground">
          {r.validStop
            ? `Spread consumes ${usd(r.spreadCost)}, ${r.riskUsd ? num((r.spreadCost / r.riskUsd) * 100, 1) : "0"}% of the risk budget.`
            : "The stop must sit below entry for a long and above entry for a short."}{" "}
          Commission and swap are not included.
        </p>
      </div>
    </div>
  );
}

function PipScaleDiagram() {
  const [move, setMove] = useState(5);
  const rows = PIP_DEFS.map((p) => ({ ...p, pips: move / p.size }));
  const maxLog = Math.log10(rows[0].pips + 1);
  return (
    <figure className="rounded-lg border border-border bg-card p-5">
      <svg viewBox="0 0 560 190" role="img" aria-labelledby="ps-t ps-d" className="w-full">
        <title id="ps-t">One gold move, three pip counts</title>
        <desc id="ps-d">The same dollar move in XAUUSD produces ten times more pips under each smaller pip definition, while the dollar profit stays identical.</desc>
        {rows.map((r, i) => {
          const y = 20 + i * 52;
          const w = Math.max(6, (Math.log10(r.pips + 1) / maxLog) * 330);
          return (
            <g key={r.id}>
              <text x={0} y={y + 22} className="fill-muted-foreground" style={{ font: "12px monospace" }}>PIP {r.id}</text>
              <rect x={100} y={y + 6} width={w} height={24} rx={3} className="fill-gold/25 stroke-gold" />
              <text x={110 + w} y={y + 23} className="fill-foreground" style={{ font: "600 13px monospace" }}>{num(r.pips, 0)} pips</text>
            </g>
          );
        })}
        <text x={100} y={182} className="fill-muted-foreground" style={{ font: "11px monospace" }}>{`SAME MOVE: $${move} PER OZ = ${usd(move * 100)} PER STANDARD LOT (LOG SCALE)`}</text>
      </svg>
      <label className="mt-4 block">
        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Price move: ${move} per oz</span>
        <input type="range" min={1} max={50} value={move} onChange={(e) => setMove(Number(e.target.value))} className="mt-2 w-full accent-[var(--gold)]" aria-label="Price move in dollars" />
      </label>
    </figure>
  );
}

const LOTS = [0.01, 0.05, 0.1, 0.5, 1, 2];

function PipTable() {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <caption className="sr-only">Gold pip value by lot size and pip definition, 100 oz per lot</caption>
        <thead className="bg-card font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="p-3 text-left">Lots</th>
            <th className="p-3 text-right">Ounces</th>
            <th className="p-3 text-right">Pip 0.01</th>
            <th className="p-3 text-right">Pip 0.10</th>
            <th className="p-3 text-right">Pip 1.00</th>
            <th className="p-3 text-right">$10 move</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {LOTS.map((l) => (
            <tr key={l} className="border-t border-border">
              <td className="p-3">{l.toFixed(2)}</td>
              <td className="p-3 text-right">{num(l * 100, 0)}</td>
              <td className="p-3 text-right">{usd(l)}</td>
              <td className="p-3 text-right text-gold">{usd(l * 10)}</td>
              <td className="p-3 text-right">{usd(l * 100)}</td>
              <td className="p-3 text-right">{usd(l * 1000)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SpreadDiagram() {
  const [stopUsd, setStopUsd] = useState(10);
  const spread = 0.3;
  const pct = (spread / stopUsd) * 100;
  const w = Math.min(500, (pct / 20) * 500);
  return (
    <figure className="rounded-lg border border-border bg-card p-5">
      <svg viewBox="0 0 560 110" role="img" aria-labelledby="sp-t sp-d" className="w-full">
        <title id="sp-t">Spread as a share of stop distance</title>
        <desc id="sp-d">A fixed 0.30 dollar spread is a large share of a tight stop and a small share of a structural stop.</desc>
        <rect x={30} y={30} width={500} height={28} rx={3} className="fill-transparent stroke-border" />
        <rect x={30} y={30} width={w} height={28} rx={3} style={{ fill: "var(--destructive)", opacity: 0.6 }} />
        <text x={30} y={22} className="fill-muted-foreground" style={{ font: "11px monospace" }}>0%</text>
        <text x={530} y={22} textAnchor="end" className="fill-muted-foreground" style={{ font: "11px monospace" }}>20%+ OF RISK</text>
        <text x={30} y={88} className="fill-foreground" style={{ font: "600 13px monospace" }}>{`$0.30 SPREAD ON A $${stopUsd} STOP = ${num(pct, 1)}% OF RISK`}</text>
      </svg>
      <label className="mt-3 block">
        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Stop distance: ${stopUsd}</span>
        <input type="range" min={1} max={30} value={stopUsd} onChange={(e) => setStopUsd(Number(e.target.value))} className="mt-2 w-full accent-[var(--gold)]" aria-label="Stop distance in dollars" />
      </label>
    </figure>
  );
}

function GoldPipCalculatorPage() {
  return (
    <Layout>
      <article className="mx-auto max-w-5xl px-4 py-12">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Market Tools / XAUUSD</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight md:text-5xl">Gold Pip Calculator: XAUUSD Pip Value and Profit</h1>
        <p className="mt-5 max-w-3xl text-lg leading-relaxed text-muted-foreground">
          One pip in gold is worth <strong className="text-foreground">$10 per standard lot</strong> when your broker defines a pip as 0.10, $1 per 0.10 lot and $0.10 per 0.01 lot. Brokers disagree on the definition, so this calculator lets you select yours and always shows the neutral reference: one dollar of price equals $100 per 100 oz lot.
        </p>

        <nav aria-label="On this page" className="sticky top-16 z-20 -mx-4 mt-8 border-y border-border bg-background/95 px-4 py-2 backdrop-blur">
          <ul className="flex gap-4 overflow-x-auto font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            {SECTIONS.map(([id, label]) => (
              <li key={id}><a href={`#${id}`} className="whitespace-nowrap hover:text-gold">{label}</a></li>
            ))}
          </ul>
        </nav>

        <section id="calculator" className="scroll-mt-32 pt-10">
          <h2 className="text-2xl font-semibold">XAUUSD pip value and profit calculator</h2>
          <p className="mt-2 mb-5 text-muted-foreground">Enter the position, then read pip value, P/L, spread drag and the R multiple against your stop.</p>
          <PipCalculator />
        </section>

        <section id="definitions" className="scroll-mt-32 pt-14">
          <h2 className="text-2xl font-semibold">Why gold pip counts disagree</h2>
          <div className="mt-4 grid gap-8 md:grid-cols-2">
            <div className="space-y-4 leading-relaxed text-muted-foreground">
              <p>For currency pairs a pip is standardised. For gold it is not. A $5 move in XAUUSD is 500 pips at one broker, 50 at another and 5 at a third. Forum threads and signal channels quoting pips without the definition are therefore meaningless.</p>
              <p><strong className="text-foreground">Professional desks measure gold in dollars per ounce.</strong> A 20 dollar stop is a 20 dollar stop on every platform. Use pips only to match what your terminal displays.</p>
            </div>
            <PipScaleDiagram />
          </div>
        </section>

        <section id="table" className="scroll-mt-32 pt-14">
          <h2 className="text-2xl font-semibold">Gold pip value table</h2>
          <p className="mt-2 mb-5 text-muted-foreground">Pip value in USD at 100 oz per lot. The highlighted column is the most common retail definition.</p>
          <PipTable />
        </section>

        <section id="formula" className="scroll-mt-32 pt-14">
          <h2 className="text-2xl font-semibold">The formula</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {[
              ["Pip value", "Pip size x oz per lot x lots", "0.10 x 100 x 0.50 = $5.00"],
              ["Profit or loss", "(Exit - Entry) x oz per lot x lots", "(2,440 - 2,400) x 100 x 0.50 = $2,000"],
              ["R multiple", "Price move / stop distance", "40 / 20 = 2.0R"],
            ].map(([t, f, e]) => (
              <div key={t} className="rounded-lg border border-border bg-card p-4">
                <div className="font-mono text-[11px] uppercase tracking-wider text-gold">{t}</div>
                <div className="mt-2 font-mono text-sm text-foreground">{f}</div>
                <div className="mt-2 font-mono text-xs text-muted-foreground">{e}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm text-muted-foreground">Gold is priced in US dollars, so no conversion applies to a USD account. For other account currencies, convert the final dollar figure at the current rate.</p>
        </section>

        <section id="costs" className="scroll-mt-32 pt-14">
          <h2 className="text-2xl font-semibold">Spread cost: the hidden pip drain</h2>
          <div className="mt-4 grid gap-8 md:grid-cols-2">
            <div className="space-y-4 leading-relaxed text-muted-foreground">
              <p>Every entry starts negative by the spread. On a tight stop that cost is a large slice of the risk budget, which is why very small gold stops rarely survive normal London and New York volatility.</p>
              <p>The live OTAS-01 model uses a 20 dollar structural stop, where a typical spread is under 2% of risk.</p>
              <div className="flex flex-wrap gap-3 pt-2">
                <Button asChild variant="gold"><Link to="/position-size-calculator">Size the position from risk <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
                <Button asChild variant="outline"><Link to="/gold-scalping-strategy">Gold scalping cost math</Link></Button>
              </div>
            </div>
            <SpreadDiagram />
          </div>
        </section>

        <section id="faq" className="scroll-mt-32 pt-14">
          <FAQ items={FAQS} />
        </section>

        <DeskCallout
          className="mt-14"
          eyebrow="Pips measure the move. Structure qualifies it."
          title="Every desk trade ships with a fixed 20 dollar structural stop."
          body="OTAS-01 publishes entry, stop and targets before price moves, and every closed trade is logged with its R multiple in the audited archive. Plug the levels straight into this calculator."
        />

        <aside className="mt-6 rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-semibold">Related references</h2>
          <ul className="mt-3 grid gap-2 text-sm md:grid-cols-2">
            <li><Link to="/what-is-xauusd" className="text-gold hover:underline">What is XAUUSD: symbol and contract sizes</Link></li>
            <li><Link to="/position-size-calculator" className="text-gold hover:underline">Gold lot size calculator</Link></li>
            <li><Link to="/gold-trading-sessions" className="text-gold hover:underline">Best time to trade gold</Link></li>
            <li><Link to="/gold-trading-strategy" className="text-gold hover:underline">Gold trading strategy playbook</Link></li>
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">Educational tool. Figures exclude commission, swap and slippage. Trading leveraged products carries substantial risk.</p>
        </aside>
      </article>
    </Layout>
  );
}
